import { useState, useMemo } from 'react'
import { Loader2, AlertCircle, CheckCircle2, AlertTriangle, Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { useParty } from '@/hooks/useParty'
import { useCandidatesByParty } from '@/hooks/useCandidatesByParty'
import { usePartyLeader } from '@/hooks/usePartyLeader'
import { useDistrictsWithRegionByNation } from '@/hooks/useDistrictsWithRegionByNation'
import { useCreateAction } from '@/hooks/useCreateAction'
import { cn } from '@/lib/utils'
import {
  INTERNAL_SUBTYPES,
  INTERNAL_EFFECTS,
  MAINTENANCE_THEMES,
  GRIEVANCE_APPROACHES,
  ROLE_CONFIGS,
  ROLE_ORDER,
  calculateMaintenanceEffects,
  calculateGrievanceEffects,
  calculatePromotionEffects,
  getLoyaltyStatus,
  shouldShowLoyaltyWarning,
  type InternalSubtype,
} from '@/constants/internal'
import type { CandidateRole } from '@/types'

interface InternalActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

export function InternalActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: InternalActionFormProps) {
  if (!open) return null

  return (
    <InternalActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function InternalActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: InternalActionFormProps) {
  // Internal subtype selection
  const [internalType, setInternalType] = useState<InternalSubtype>('loyalty_maintenance')

  // Loyalty maintenance fields
  const [theme, setTheme] = useState<string>('')

  // Address grievance fields
  const [grievanceCandidateId, setGrievanceCandidateId] = useState<string>('')
  const [approach, setApproach] = useState<string>('')

  // Promote candidate fields
  const [promoteCandidateId, setPromoteCandidateId] = useState<string>('')
  const [newRole, setNewRole] = useState<CandidateRole | ''>('')
  const [districtId, setDistrictId] = useState<string>('')

  // Common fields
  const [intensity, setIntensity] = useState<number>(1)

  // Fetch current party to get nation_id
  const { party, isLoading: partyLoading } = useParty(partyId)

  // Fetch all active candidates for the party
  const { candidates, isLoading: candidatesLoading } = useCandidatesByParty(partyId)

  // Fetch current party leader
  const { leader: currentLeader, isLoading: leaderLoading } = usePartyLeader(partyId)

  // Fetch districts for regional candidate assignment
  const { districts, isLoading: districtsLoading } = useDistrictsWithRegionByNation(party?.nation_id)

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Sort candidates by loyalty (ascending) for address grievance
  const candidatesSortedByLoyalty = useMemo(() => {
    if (!candidates) return []
    return [...candidates].sort((a, b) => a.loyalty - b.loyalty)
  }, [candidates])

  // Get selected candidate for grievance
  const selectedGrievanceCandidate = useMemo(() => {
    if (!grievanceCandidateId || !candidates) return null
    return candidates.find((c) => c.id === grievanceCandidateId)
  }, [grievanceCandidateId, candidates])

  // Get selected candidate for promotion
  const selectedPromoteCandidate = useMemo(() => {
    if (!promoteCandidateId || !candidates) return null
    return candidates.find((c) => c.id === promoteCandidateId)
  }, [promoteCandidateId, candidates])

  // Get district info for selected district
  const selectedDistrict = useMemo(() => {
    if (!districtId || !districts) return null
    return districts.find((d) => d.id === districtId)
  }, [districtId, districts])

  // Find candidate currently assigned to selected district
  const candidateInSelectedDistrict = useMemo(() => {
    if (!districtId || !candidates) return null
    return candidates.find(
      (c) => c.assigned_district_id === districtId && c.id !== promoteCandidateId
    )
  }, [districtId, candidates, promoteCandidateId])

  // Check if all candidates have healthy loyalty
  const allCandidatesHealthy = useMemo(() => {
    if (!candidates || candidates.length === 0) return true
    return candidates.every((c) => c.loyalty >= INTERNAL_EFFECTS.LOYALTY_MODERATE)
  }, [candidates])

  // Check if any candidates have low loyalty
  const hasUnhappyCandidates = useMemo(() => {
    if (!candidates) return false
    return candidates.some((c) => c.loyalty < INTERNAL_EFFECTS.LOYALTY_LOW)
  }, [candidates])

  // Calculate effect preview based on selections
  const effectPreview = useMemo(() => {
    switch (internalType) {
      case 'loyalty_maintenance': {
        const effects = calculateMaintenanceEffects(intensity)
        const candidateCount = candidates?.length ?? 0
        return {
          description: `+${effects.loyaltyBoost} loyalty to all ${candidateCount} candidate${candidateCount !== 1 ? 's' : ''}`,
          loyaltyChange: effects.loyaltyBoost,
          isPositive: true,
        }
      }
      case 'address_grievance': {
        const effects = calculateGrievanceEffects(intensity)
        const currentLoyalty = selectedGrievanceCandidate?.loyalty ?? 0
        const estimatedNew = Math.min(100, currentLoyalty + effects.loyaltyBoost)
        return {
          description: selectedGrievanceCandidate
            ? `+${effects.loyaltyBoost} loyalty to ${selectedGrievanceCandidate.name} (${currentLoyalty} → ~${estimatedNew})`
            : `+${effects.loyaltyBoost} loyalty to selected candidate`,
          loyaltyChange: effects.loyaltyBoost,
          isPositive: true,
          currentLoyalty,
          estimatedLoyalty: estimatedNew,
        }
      }
      case 'promote_candidate': {
        if (!selectedPromoteCandidate || !newRole) {
          return {
            description: 'Select candidate and new role',
            loyaltyChange: 0,
            isPositive: true,
          }
        }
        const effects = calculatePromotionEffects(
          selectedPromoteCandidate.role,
          newRole as CandidateRole
        )
        return {
          description: effects.description,
          loyaltyChange: effects.loyaltyChange,
          isPositive: effects.loyaltyChange >= 0,
          changeType: effects.changeType,
        }
      }
    }
  }, [internalType, intensity, candidates, selectedGrievanceCandidate, selectedPromoteCandidate, newRole])

  // AP cost calculation
  const apCost = useMemo(() => {
    if (internalType === 'promote_candidate') {
      return INTERNAL_EFFECTS.PROMOTE_AP_COST
    }
    return intensity
  }, [internalType, intensity])

  // Validation
  const canSubmit = useMemo(() => {
    if (isCreating || apCost > availableAP) return false
    if (!candidates || candidates.length === 0) return false

    switch (internalType) {
      case 'loyalty_maintenance':
        return true
      case 'address_grievance':
        return !!grievanceCandidateId
      case 'promote_candidate': {
        if (!promoteCandidateId || !newRole) return false
        // New role must be different from current role
        if (selectedPromoteCandidate?.role === newRole) return false
        // If regional candidate, must have district selected
        if (newRole === 'regional_candidate' && !districtId) return false
        return true
      }
    }
  }, [
    isCreating,
    apCost,
    availableAP,
    candidates,
    internalType,
    grievanceCandidateId,
    promoteCandidateId,
    newRole,
    selectedPromoteCandidate,
    districtId,
  ])

  const apAfterAction = availableAP - apCost
  const minAP = INTERNAL_EFFECTS.MIN_AP
  const maxAP = Math.min(INTERNAL_EFFECTS.MAX_AP, availableAP)

  const handleSubmit = () => {
    if (!canSubmit) return

    const parameters: Record<string, unknown> = {}

    switch (internalType) {
      case 'loyalty_maintenance':
        if (theme) {
          parameters.theme = theme
        }
        break
      case 'address_grievance':
        parameters.candidate_id = grievanceCandidateId
        if (selectedGrievanceCandidate) {
          parameters.candidate_name = selectedGrievanceCandidate.name
          parameters.current_loyalty = selectedGrievanceCandidate.loyalty
        }
        if (approach) {
          parameters.approach = approach
        }
        break
      case 'promote_candidate':
        parameters.candidate_id = promoteCandidateId
        parameters.new_role = newRole
        if (selectedPromoteCandidate) {
          parameters.candidate_name = selectedPromoteCandidate.name
          parameters.previous_role = selectedPromoteCandidate.role
        }
        if (newRole === 'regional_candidate' && districtId) {
          parameters.district_id = districtId
          parameters.district_name = selectedDistrict?.name
        }
        // Record if we're replacing another candidate
        if (candidateInSelectedDistrict) {
          parameters.replaced_candidate_id = candidateInSelectedDistrict.id
          parameters.replaced_candidate_name = candidateInSelectedDistrict.name
        }
        // Record if we're replacing the party leader
        if (newRole === 'party_leader' && currentLeader && currentLeader.id !== promoteCandidateId) {
          parameters.replaced_leader_id = currentLeader.id
          parameters.replaced_leader_name = currentLeader.name
        }
        break
    }

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'internal',
      action_subtype: internalType,
      target_type: internalType === 'loyalty_maintenance' ? 'party' : 'candidate',
      target_id:
        internalType === 'loyalty_maintenance'
          ? partyId
          : internalType === 'address_grievance'
            ? grievanceCandidateId
            : promoteCandidateId,
      action_points_spent: apCost,
      parameters,
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  // Reset type-specific state when internal type changes
  const handleInternalTypeChange = (newType: InternalSubtype) => {
    setInternalType(newType)
    // Reset fields when switching types
    if (newType === 'loyalty_maintenance') {
      setTheme('')
      setIntensity(1)
    } else if (newType === 'address_grievance') {
      setGrievanceCandidateId('')
      setApproach('')
      setIntensity(1)
    } else if (newType === 'promote_candidate') {
      setPromoteCandidateId('')
      setNewRole('')
      setDistrictId('')
    }
  }

  // Loading state
  const isDataLoading = partyLoading || candidatesLoading || leaderLoading || districtsLoading

  // Helper to render loyalty badge
  const renderLoyaltyBadge = (loyalty: number) => {
    const status = getLoyaltyStatus(loyalty)
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
          status.bgClass,
          status.textClass
        )}
      >
        {shouldShowLoyaltyWarning(loyalty) && <AlertTriangle className="h-3 w-3" />}
        {loyalty}
      </span>
    )
  }

  // Helper to render role badge
  const renderRoleBadge = (role: CandidateRole) => {
    const config = ROLE_CONFIGS[role]
    if (role === 'none') return null
    return (
      <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
        {config.label}
      </span>
    )
  }

  // Empty state: no candidates
  if (!isDataLoading && (!candidates || candidates.length === 0)) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🏛️</span> Internal Management
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-8">
            <Info className="h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm font-medium">No Candidates Available</p>
              <p className="mt-1 text-sm text-muted-foreground">
                You need to recruit candidates before using internal management actions.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleBack}>
              Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🏛️</span> Configure Internal Action
          </DialogTitle>
          <DialogDescription>
            Manage party loyalty, address grievances, and assign candidate roles.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Internal Type Selection */}
          <div className="space-y-3">
            <Label>Action Type</Label>
            <div className="grid grid-cols-1 gap-2">
              {(
                Object.entries(INTERNAL_SUBTYPES) as [
                  InternalSubtype,
                  (typeof INTERNAL_SUBTYPES)[InternalSubtype],
                ][]
              ).map(([type, config]) => {
                const isSelected = internalType === type
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleInternalTypeChange(type)}
                    className={cn(
                      'rounded-lg border-2 p-3 text-left transition-all',
                      'hover:border-primary/50 hover:bg-accent/50',
                      isSelected
                        ? 'border-primary bg-accent'
                        : 'border-border bg-background'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{config.icon}</span>
                        <span className="font-medium">{config.label}</span>
                      </div>
                      {isSelected && (
                        <div className="rounded-full bg-primary p-0.5 text-primary-foreground">
                          <CheckCircle2 className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{config.description}</p>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Loyalty Maintenance Options */}
          {internalType === 'loyalty_maintenance' && (
            <>
              {/* All healthy advisory */}
              {allCandidatesHealthy && (
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900/30 dark:bg-green-900/20">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">
                    All candidates have healthy loyalty levels. Maintenance will help maintain
                    morale.
                  </span>
                </div>
              )}

              {/* Theme Selector (optional flavor) */}
              <div className="space-y-2">
                <Label htmlFor="theme-select">
                  Event Theme <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger id="theme-select">
                    <SelectValue placeholder="Select a theme..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No specific theme</SelectItem>
                    {MAINTENANCE_THEMES.map((t) => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {theme && (
                  <p className="text-xs text-muted-foreground">
                    {MAINTENANCE_THEMES.find((t) => t.key === theme)?.description}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Address Grievance Options */}
          {internalType === 'address_grievance' && (
            <>
              {/* No unhappy candidates advisory */}
              {!hasUnhappyCandidates && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/30 dark:bg-blue-900/20">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    All candidates have healthy loyalty levels. Consider using Loyalty Maintenance
                    instead.
                  </span>
                </div>
              )}

              {/* Candidate Selector */}
              <div className="space-y-2">
                <Label htmlFor="grievance-candidate-select">Select Candidate</Label>
                {candidatesLoading ? (
                  <div className="flex h-9 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Select value={grievanceCandidateId} onValueChange={setGrievanceCandidateId}>
                    <SelectTrigger id="grievance-candidate-select">
                      <SelectValue placeholder="Choose a candidate..." />
                    </SelectTrigger>
                    <SelectContent>
                      {candidatesSortedByLoyalty.map((candidate) => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          <div className="flex w-full items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span>{candidate.name}</span>
                              {renderRoleBadge(candidate.role)}
                            </div>
                            {renderLoyaltyBadge(candidate.loyalty)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedGrievanceCandidate && (
                  <p className="text-xs text-muted-foreground">
                    Current loyalty: {selectedGrievanceCandidate.loyalty}/100 (
                    {getLoyaltyStatus(selectedGrievanceCandidate.loyalty).label})
                  </p>
                )}
              </div>

              {/* Approach Selector (optional flavor) */}
              <div className="space-y-2">
                <Label htmlFor="approach-select">
                  Approach <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Select value={approach} onValueChange={setApproach}>
                  <SelectTrigger id="approach-select">
                    <SelectValue placeholder="Select an approach..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No specific approach</SelectItem>
                    {GRIEVANCE_APPROACHES.map((a) => (
                      <SelectItem key={a.key} value={a.key}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {approach && (
                  <p className="text-xs text-muted-foreground">
                    {GRIEVANCE_APPROACHES.find((a) => a.key === approach)?.description}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Promote Candidate Options */}
          {internalType === 'promote_candidate' && (
            <>
              {/* Candidate Selector */}
              <div className="space-y-2">
                <Label htmlFor="promote-candidate-select">Select Candidate</Label>
                {candidatesLoading ? (
                  <div className="flex h-9 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Select value={promoteCandidateId} onValueChange={setPromoteCandidateId}>
                    <SelectTrigger id="promote-candidate-select">
                      <SelectValue placeholder="Choose a candidate..." />
                    </SelectTrigger>
                    <SelectContent>
                      {candidates?.map((candidate) => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          <div className="flex w-full items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span>{candidate.name}</span>
                              {renderRoleBadge(candidate.role)}
                            </div>
                            {renderLoyaltyBadge(candidate.loyalty)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedPromoteCandidate && (
                  <p className="text-xs text-muted-foreground">
                    Current role: {ROLE_CONFIGS[selectedPromoteCandidate.role].label}
                    {selectedPromoteCandidate.assigned_district_id && (
                      <> (assigned to district)</>
                    )}
                  </p>
                )}
              </div>

              {/* Role Selector */}
              {promoteCandidateId && (
                <div className="space-y-2">
                  <Label htmlFor="role-select">New Role</Label>
                  <Select value={newRole} onValueChange={(v) => setNewRole(v as CandidateRole)}>
                    <SelectTrigger id="role-select">
                      <SelectValue placeholder="Choose a role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_ORDER.map((role) => {
                        const config = ROLE_CONFIGS[role]
                        const isCurrentRole = selectedPromoteCandidate?.role === role
                        // Disable party_leader if another candidate has it
                        const isLeaderTaken =
                          role === 'party_leader' &&
                          currentLeader &&
                          currentLeader.id !== promoteCandidateId

                        return (
                          <SelectItem
                            key={role}
                            value={role}
                            disabled={isCurrentRole}
                            className={cn(isCurrentRole && 'opacity-50')}
                          >
                            <div className="flex items-center gap-2">
                              <span>{config.label}</span>
                              {isCurrentRole && (
                                <span className="text-xs text-muted-foreground">(current)</span>
                              )}
                              {isLeaderTaken && (
                                <span className="text-xs text-orange-600 dark:text-orange-400">
                                  (will replace {currentLeader.name})
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                  {newRole && (
                    <p className="text-xs text-muted-foreground">
                      {ROLE_CONFIGS[newRole as CandidateRole].description}
                    </p>
                  )}
                </div>
              )}

              {/* District Selector (for regional_candidate) */}
              {newRole === 'regional_candidate' && (
                <div className="space-y-2">
                  <Label htmlFor="district-select">Assign to District</Label>
                  {districtsLoading ? (
                    <div className="flex h-9 items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Select value={districtId} onValueChange={setDistrictId}>
                      <SelectTrigger id="district-select">
                        <SelectValue placeholder="Choose a district..." />
                      </SelectTrigger>
                      <SelectContent>
                        {districts?.map((district) => {
                          const assignedCandidate = candidates?.find(
                            (c) =>
                              c.assigned_district_id === district.id && c.id !== promoteCandidateId
                          )
                          return (
                            <SelectItem key={district.id} value={district.id}>
                              <div className="flex items-center gap-2">
                                <span>{district.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({district.region.name})
                                </span>
                                {assignedCandidate && (
                                  <span className="text-xs text-orange-600 dark:text-orange-400">
                                    ({assignedCandidate.name} assigned)
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Warning: Replacing party leader */}
              {newRole === 'party_leader' &&
                currentLeader &&
                currentLeader.id !== promoteCandidateId && (
                  <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/30 dark:bg-orange-900/20">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" />
                    <div className="text-sm text-orange-700 dark:text-orange-300">
                      <p className="font-medium">This will replace the current party leader</p>
                      <p className="mt-1">
                        {currentLeader.name} will be demoted and may lose significant loyalty (
                        {INTERNAL_EFFECTS.DEMOTION_FROM_LEADER_LOYALTY}).
                      </p>
                    </div>
                  </div>
                )}

              {/* Warning: Replacing regional candidate */}
              {candidateInSelectedDistrict && (
                <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/30 dark:bg-orange-900/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" />
                  <div className="text-sm text-orange-700 dark:text-orange-300">
                    <p className="font-medium">This will replace the current regional candidate</p>
                    <p className="mt-1">
                      {candidateInSelectedDistrict.name} will be removed from{' '}
                      {selectedDistrict?.name} and may lose loyalty (
                      {INTERNAL_EFFECTS.REPLACEMENT_LOYALTY}).
                    </p>
                  </div>
                </div>
              )}

              {/* Warning: Demoting party leader */}
              {selectedPromoteCandidate?.role === 'party_leader' && newRole && newRole !== 'party_leader' && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/30 dark:bg-red-900/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                  <div className="text-sm text-red-700 dark:text-red-300">
                    <p className="font-medium">Removing the party leader</p>
                    <p className="mt-1">
                      This will significantly impact {selectedPromoteCandidate.name}'s loyalty (
                      {INTERNAL_EFFECTS.DEMOTION_FROM_LEADER_LOYALTY}) and may affect party
                      stability.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Intensity Slider (only for loyalty_maintenance and address_grievance) */}
          {internalType !== 'promote_candidate' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Intensity</Label>
                <span className="text-sm font-medium">{intensity} AP</span>
              </div>
              <Slider
                value={[intensity]}
                onValueChange={([value]) => setIntensity(value)}
                min={minAP}
                max={maxAP}
                step={1}
                disabled={availableAP < minAP}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1 - Standard</span>
                <span>2 - Intensive</span>
              </div>
            </div>
          )}

          {/* Effect Preview */}
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <h4 className="text-sm font-medium">Expected Effect</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Loyalty change:</span>
                <span
                  className={cn(
                    'font-medium',
                    effectPreview.isPositive
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  )}
                >
                  {effectPreview.description}
                </span>
              </div>
            </div>
          </div>

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}>
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Insufficient AP warning */}
          {availableAP < minAP && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>
                Internal actions require at least {minAP} AP. You have {availableAP} AP available.
              </span>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error.message || 'Failed to queue action'}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleBack} disabled={isCreating}>
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue Internal Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
